
import { Component, Vue } from 'vue-property-decorator';
import ListLayout from '@/libs/common/components/ListLayout/ListLayout.vue';
import {
    ListLayoutConfig,
    ListLayoutViewType,
    TableConfig,
    KanbanConfig,
    TimelineConfig,
    CalendarConfig,
    FilterDefinition,
} from '@/libs/common/components/ListLayout/core/list-layout.types';
import moment from 'moment';

// Mock Store pro testování
class TestStore {
    private items: any[] = [];

    constructor() {
        this.generateMockData();
    }

    generateMockData() {
        const statuses = ['new', 'in-progress', 'review', 'done'];
        const priorities = ['low', 'medium', 'high'];
        const assignees = ['Jan Novák', 'Petra Svobodová', 'Tomáš Dvořák', 'Marie Černá'];

        for (let i = 1; i <= 100; i++) {
            const startDate = moment()
                .add(Math.floor(Math.random() * 60) - 30, 'days')
                .toDate();
            const endDate = moment(startDate)
                .add(Math.floor(Math.random() * 14) + 1, 'days')
                .toDate();

            this.items.push({
                id: `item-${i}`,
                name: `Testovací úkol ${i}`,
                description: `Detailní popis úkolu číslo ${i}. Toto je delší text s více informacemi o tom, co je třeba udělat.`,
                status: statuses[Math.floor(Math.random() * statuses.length)],
                priority: priorities[Math.floor(Math.random() * priorities.length)],
                assignee: assignees[Math.floor(Math.random() * assignees.length)],
                createdAt: moment()
                    .subtract(Math.floor(Math.random() * 60), 'days')
                    .toDate(),
                startDate,
                endDate,
            });
        }
    }

    async query(params: any) {
        let filtered = [...this.items];

        // Apply filters
        if (params.filters) {
            Object.keys(params.filters).forEach((key) => {
                const value = params.filters[key];
                if (value && key === 'search') {
                    filtered = filtered.filter((item) => item.name.toLowerCase().includes(value.toLowerCase()));
                } else if (value) {
                    filtered = filtered.filter((item) => item[key] === value);
                }
            });
        }

        // Apply sorting
        if (params.sort) {
            filtered.sort((a, b) => {
                const aVal = a[params.sort.column];
                const bVal = b[params.sort.column];
                const mult = params.sort.direction === 'asc' ? 1 : -1;
                return aVal > bVal ? mult : aVal < bVal ? -mult : 0;
            });
        }

        const skip = params.skip || 0;
        const take = params.take || 50;
        const data = filtered.slice(skip, skip + take);

        return new Promise((resolve) => {
            setTimeout(() => {
                resolve({
                    data,
                    total: filtered.length,
                });
            }, 300);
        });
    }

    async getAll(params: any) {
        let filtered = [...this.items];

        if (params.filters) {
            Object.keys(params.filters).forEach((key) => {
                const value = params.filters[key];
                if (value && key === 'search') {
                    filtered = filtered.filter((item) => item.name.toLowerCase().includes(value.toLowerCase()));
                } else if (value) {
                    filtered = filtered.filter((item) => item[key] === value);
                }
            });
        }

        return new Promise((resolve) => {
            setTimeout(() => {
                resolve(filtered);
            }, 300);
        });
    }

    async export(params: any) {
        console.log('Exporting data with format:', params.format);
        return new Promise<void>((resolve) => {
            setTimeout(() => {
                resolve();
            }, 1000);
        });
    }

    addItem(item: any) {
        this.items.unshift({
            ...item,
            id: `item-${this.items.length + 1}`,
            createdAt: new Date(),
            startDate: new Date(),
            endDate: moment().add(7, 'days').toDate(),
        });
    }
}

@Component({
    components: {
        ListLayout,
    },
})
export default class ListLayoutTest extends Vue {
    testStore = new TestStore();
    showAddDialog = false;

    newItem = {
        name: '',
        description: '',
        status: 'new',
        priority: 'medium',
        assignee: 'Test User',
    };

    rules = {
        required: (v: any) => !!v || this.$i18n.t('common.thisFileIsRequired'),
    };

    statusOptions = [
        { text: 'Nové', value: 'new' },
        { text: 'V řešení', value: 'in-progress' },
        { text: 'Kontrola', value: 'review' },
        { text: 'Hotovo', value: 'done' },
    ];

    priorityOptions = [
        { text: 'Nízká', value: 'low' },
        { text: 'Střední', value: 'medium' },
        { text: 'Vysoká', value: 'high' },
    ];

    layoutConfig: ListLayoutConfig = {
        enabledViews: [
            ListLayoutViewType.Table,
            ListLayoutViewType.Kanban,
            ListLayoutViewType.Timeline,
            ListLayoutViewType.Calendar,
        ],
        defaultView: ListLayoutViewType.Table,
        enableFilters: true,
        enableExports: true,
        enableSavedFilters: true,
        storageKey: 'list-layout-test',
    };

    tableConfig: TableConfig = {
        columns: [
            { text: 'Název', value: 'name', type: 'text', sortable: true },
            { text: 'Popis', value: 'description', type: 'text' },
            { text: 'Stav', value: 'status', type: 'select', sortable: true },
            { text: 'Priorita', value: 'priority', type: 'select', sortable: true },
            { text: 'Přiřazeno', value: 'assignee', type: 'text' },
            { text: 'Vytvořeno', value: 'createdAt', type: 'date', sortable: true },
        ],
        serverSidePagination: true,
        pageSize: 25,
        pageSizeOptions: [10, 25, 50, 100],
        sortable: true,
        selectable: true,
    };

    kanbanConfig: KanbanConfig = {
        groupByProperty: 'status',
        columns: [
            { name: 'Nové', propertyValue: 'new', color: '#2196f3', icon: 'mdi-new-box' },
            { name: 'V řešení', propertyValue: 'in-progress', color: '#ff9800', icon: 'mdi-progress-clock' },
            { name: 'Kontrola', propertyValue: 'review', color: '#9c27b0', icon: 'mdi-eye' },
            { name: 'Hotovo', propertyValue: 'done', color: '#4caf50', icon: 'mdi-check-circle' },
        ],
        enableDragDrop: true,
        cardHeight: 150,
    };

    timelineConfig: TimelineConfig = {
        startDateProperty: 'startDate',
        endDateProperty: 'endDate',
        labelProperty: 'name',
        rowHeight: 50,
        segmentTypes: [{ type: 'default', label: 'Úkol', color: '#2196f3' }],
        enableZoom: true,
    };

    calendarConfig: CalendarConfig = {
        dateProperty: 'startDate',
        startTimeProperty: 'startDate',
        endTimeProperty: 'endDate',
        titleProperty: 'name',
        colorProperty: 'status',
        viewMode: 'month',
        enableDragDrop: false,
        weekStartsOn: 1,
    };

    filterDefinitions: FilterDefinition[] = [
        {
            id: 'search',
            label: 'Vyhledat',
            type: 'text',
            property: 'search',
            placeholder: 'Vyhledat v názvu...',
            visible: true,
        },
        {
            id: 'status',
            label: 'Stav',
            type: 'select',
            property: 'status',
            options: this.statusOptions,
            visible: true,
        },
        {
            id: 'priority',
            label: 'Priorita',
            type: 'select',
            property: 'priority',
            options: this.priorityOptions,
            visible: true,
            advanced: true,
        },
    ];

    handleItemClick(item: any) {
        console.log('Item clicked:', item);
        // Toast notification: Kliknuto na: ${item.name}
    }

    handleItemMove(payload: any) {
        console.log('Item moved:', payload);
        // Toast notification: Položka přesunuta
    }

    handleSelectionChange(selectedItems: string[]) {
        console.log('Selection changed:', selectedItems);
    }

    handleDataLoaded(items: any[]) {
        console.log('Data loaded:', items.length, 'items');
    }

    handleExportComplete(format: string) {
        console.log(`Export do ${format.toUpperCase()} dokončen`);
    }

    handleExportError(error: any) {
        console.error('Chyba při exportu:', error);
    }

    addItem() {
        this.testStore.addItem(this.newItem);
        this.showAddDialog = false;
        this.newItem = {
            name: '',
            description: '',
            status: 'new',
            priority: 'medium',
            assignee: 'Test User',
        };
        console.log('Položka byla přidána');
        this.refreshData();
    }

    refreshData() {
        console.log('Data aktualizována');
        window.location.reload();
    }

    getStatusColor(status: string): string {
        const colors: Record<string, string> = {
            new: '#2196f3',
            'in-progress': '#ff9800',
            review: '#9c27b0',
            done: '#4caf50',
        };
        return colors[status] || '#757575';
    }

    getStatusIcon(status: string): string {
        const icons: Record<string, string> = {
            new: 'mdi-new-box',
            'in-progress': 'mdi-progress-clock',
            review: 'mdi-eye',
            done: 'mdi-check-circle',
        };
        return icons[status] || 'mdi-circle';
    }

    getStatusLabel(status: string): string {
        const labels: Record<string, string> = {
            new: 'Nové',
            'in-progress': 'V řešení',
            review: 'Kontrola',
            done: 'Hotovo',
        };
        return labels[status] || status;
    }

    getPriorityColor(priority: string): string {
        const colors: Record<string, string> = {
            low: '#4caf50',
            medium: '#ff9800',
            high: '#f44336',
        };
        return colors[priority] || '#757575';
    }

    getPriorityLabel(priority: string): string {
        const labels: Record<string, string> = {
            low: 'Nízká',
            medium: 'Střední',
            high: 'Vysoká',
        };
        return labels[priority] || priority;
    }

    formatDate(date: Date): string {
        return moment(date).format('DD.MM.YYYY');
    }

    formatTime(date: Date): string {
        return moment(date).format('HH:mm');
    }
}
